import { Alert } from '@/components/ui/alert';
import { useConnectedAssistantActivityQuery } from '@/hooks/queries/use-connected-assistant-activity';
import { Badge, Button, HStack, Skeleton, Stack, Text } from '@chakra-ui/react';
import type {
  McpConnectionActivityDecision,
  McpConnectionActivityEvent,
  McpConnectionActivityItem,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { formatAssistantDate } from './format-assistant-date';

const connectionEventKeys: Partial<Record<McpConnectionActivityEvent, string>> = {
  connection_authorized: 'mcp.settings.activity.events.connection_authorized',
  connection_reduced: 'mcp.settings.activity.events.connection_reduced',
  connection_revoked: 'mcp.settings.activity.events.connection_revoked',
};

/** Only an outcome worth reading gets a badge; an accepted action already reads as done. */
const decisionBadges: Partial<
  Record<McpConnectionActivityDecision, { key: string; colorPalette: string }>
> = {
  succeeded: { key: 'mcp.settings.activity.decisions.succeeded', colorPalette: 'green' },
  partially_succeeded: {
    key: 'mcp.settings.activity.decisions.partially_succeeded',
    colorPalette: 'orange',
  },
  failed: { key: 'mcp.settings.activity.decisions.failed', colorPalette: 'red' },
  needs_reconciliation: {
    key: 'mcp.settings.activity.decisions.needs_reconciliation',
    colorPalette: 'orange',
  },
  rejected: { key: 'mcp.settings.activity.decisions.rejected', colorPalette: 'red' },
};

function ActivityLine({ item }: { item: McpConnectionActivityItem }) {
  const { t, i18n } = useTranslation();
  const eventKey = connectionEventKeys[item.event];
  // A capability key this build has no words for reads as a generic action, never as its raw key.
  const label = eventKey
    ? t(eventKey)
    : t(`mcp.settings.activity.capabilities.${(item.capability ?? 'other').replaceAll('.', '_')}`, {
        defaultValue: t('mcp.settings.activity.capabilities.other'),
      });
  const badge = decisionBadges[item.decision];
  return (
    <Stack as="li" gap={1} py={3} borderTopWidth="1px" _first={{ borderTopWidth: 0, pt: 0 }}>
      <HStack gap={2} justify="space-between" align="start">
        <Text fontSize="sm">{[label, item.brandName].filter(Boolean).join(' · ')}</Text>
        {badge && (
          <Badge size="sm" variant="subtle" colorPalette={badge.colorPalette} flexShrink={0}>
            {t(badge.key)}
          </Badge>
        )}
      </HStack>
      <Text color="fg.muted" fontSize="xs">
        {formatAssistantDate(item.at, i18n.language)}
      </Text>
    </Stack>
  );
}

export function ConnectedAssistantActivity({
  connectionId,
  enabled,
}: {
  connectionId: string;
  enabled: boolean;
}) {
  const { t } = useTranslation();
  const query = useConnectedAssistantActivityQuery(connectionId, { enabled });
  if (query.isLoading) return <Skeleton h="80px" aria-label={t('mcp.loading')} />;
  if (query.isError || !query.data)
    return (
      <Stack gap={3}>
        <Alert status="error" title={t('mcp.settings.activity.loadError')} />
        <Button
          variant="outline"
          size="sm"
          alignSelf="start"
          onClick={() => void query.refetch()}
        >
          {t('mcp.retry')}
        </Button>
      </Stack>
    );
  if (query.data.items.length === 0)
    return (
      <Text color="fg.muted" fontSize="sm">
        {t('mcp.settings.activity.empty')}
      </Text>
    );
  return (
    <Stack as="ul" listStyleType="none" gap={0}>
      {query.data.items.map((item, index) => (
        <ActivityLine key={`${item.at}:${index}`} item={item} />
      ))}
    </Stack>
  );
}
